<script setup lang="ts">
import device from '@@/bits/device'
import { __ } from '@@/bits/intl'
import OzBaseButton from '@@/library/v4/components/OzBaseButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzTooltip from '@@/library/v4/components/OzTooltip.vue'
import { useSurfaceTimelineFormatStore } from '@@/pinia/surface_timeline_format_store'
import { offset, shift } from '@floating-ui/vue'
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

defineProps<{
  label: string
  backgroundLuminance: string
  colorScheme: string
  isMobileLandscapeScreen: boolean
}>()

defineEmits<{
  (e: 'click'): void
}>()

const { dashedLineMarginPercentFromViewportBottom, adjustedPrimaryBackgroundColorForCircleAndPlusButton } = storeToRefs(
  useSurfaceTimelineFormatStore(),
)

const addPostButtonRef = ref<typeof OzBaseButton | null>(null)
const addPostButtonRefEl = computed(() => addPostButtonRef.value?.$el)

const xTooltip = ref(false)
</script>

<template>
  <OzBaseButton
    ref="addPostButtonRef"
    :aria-label="label"
    :class="[
      'bg-transparent p-1.5 h-7.5 w-7.5 rounded-lg group',
      isMobileLandscapeScreen && ['mb-5.5'],
      !isMobileLandscapeScreen && [
        'mb-35',
        device.app && '@tablet-portrait/surface-container:mb-[212px]',
        !device.app && [
          // 3.5px: 9.5px dashed lines margin bottom - 6px plus button padding bottom
          dashedLineMarginPercentFromViewportBottom === 0.25 &&
            '@tablet-portrait/surface-container:mb-[calc(100vh*0.25-3.5px)]',
          dashedLineMarginPercentFromViewportBottom === 0.36 &&
            '@tablet-portrait/surface-container:mb-[calc(100vh*0.36-3.5px)]',
        ],
      ],
      // Focus
      'focus-visible:ring-2',
      colorScheme === 'light' ? 'focus-visible:ring-grape-500' : 'focus-visible:ring-canary-500',
    ]"
    @click="$emit('click')"
    @focus="xTooltip = true"
    @blur="xTooltip = false"
    @mouseenter="xTooltip = true"
    @mouseleave="xTooltip = false"
  >
    <OzIcon
      :size="18"
      name="timeline_add_post"
      class="opacity-90"
      :style="{ color: adjustedPrimaryBackgroundColorForCircleAndPlusButton }"
    />
    <OzTooltip
      v-if="!device.mobile && xTooltip"
      class="z-50 whitespace-nowrap"
      :anchor="addPostButtonRefEl"
      :dark-mode="backgroundLuminance === 'dark'"
      :config="{
        placement: 'top',
        middleware: [
          offset({
            mainAxis: 6,
          }),
          shift({
            padding: 8,
          }),
        ],
      }"
    >
      <p>{{ __('Click to add post here') }}</p>
    </OzTooltip>
  </OzBaseButton>
</template>
